"use client";

import { useSyncExternalStore } from "react";

import { sendGTMEvent } from "@next/third-parties/google";
import Link from "next/link";

import { SIGNUP_CTA, TALK_TO_HUMAN_CTA } from "@/app/(marketing)/constants";
import { LOGIN_REDIRECT_URL } from "@/app/lib/constants";
import { useProtectedEmail } from "@/app/lib/hooks/useProtectedEmail";

interface NavLink {
  label: string;
  /** Undefined for Talk to a human until the browser has built its mailto. */
  href: string | undefined;
  component: any;
  buttonProps: Record<string, any>;
  onClick?: () => void;
  isCta?: boolean;
}

function subscribeToStorage(onChange: () => void) {
  window.addEventListener("storage", onChange);

  return () => window.removeEventListener("storage", onChange);
}

// A visitor with an active organization is signed in, so the nav offers the
// way back into the app instead of Login. The key is only present when the
// visitor arrives from the app by client-side navigation: on a full page load
// the root OrganizationProvider, which loads no organization on a marketing
// route, clears it before this layout hydrates.
const getHasActiveOrganization = () => !!localStorage.getItem("currentOrganization");

// The server cannot see localStorage, so it always renders Login.
const getServerHasActiveOrganization = () => false;

export const useNavLinks = (): NavLink[] => {
  const isLoggedIn = useSyncExternalStore(
    subscribeToStorage,
    getHasActiveOrganization,
    getServerHasActiveOrganization,
  );
  const founderEmail = useProtectedEmail(TALK_TO_HUMAN_CTA.email);

  return [
    // { label: "Use Cases", href: "/use-cases", component: Link, buttonProps: { variant: "subtle" } },
    {
      label: "Platform",
      href: "/platform",
      component: Link,
      buttonProps: { variant: "subtle" },
    },
    { label: "Pricing", href: "/pricing", component: Link, buttonProps: { variant: "subtle" } },
    isLoggedIn
      ? {
          label: "Back to App",
          href: LOGIN_REDIRECT_URL,
          component: Link,
          buttonProps: { variant: "subtle" },
        }
      : {
          label: "Login",
          href: "/auth/login",
          component: Link,
          buttonProps: { variant: "subtle" },
        },
    {
      label: TALK_TO_HUMAN_CTA.label,
      href: founderEmail ? `mailto:${founderEmail}` : undefined,
      component: "a",
      buttonProps: { variant: "subtle" },
    },
    {
      label: SIGNUP_CTA.label,
      href: SIGNUP_CTA.href,
      component: Link,
      isCta: true,
      buttonProps: {
        variant: "gradient",
        gradient: { from: "blue", to: "cyan", deg: 90 },
        ml: "sm",
      },
      onClick: () =>
        sendGTMEvent({
          ...SIGNUP_CTA.gtm,
          location: "nav",
          button_style: "gradient",
        }),
    },
  ];
};
